import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import LinkButtonNodeView from './LinkButtonNodeView.vue'
import { normalizeHexColor } from './colorAttributes'
import { safeLinkUrl } from './linkAttributes'
import { DEFAULT_LINK_BUTTON_COLOR, DEFAULT_LINK_BUTTON_VARIANT, LINK_BUTTON_STYLES, linkButtonStyle, normalizeLinkButtonVariant } from './linkButtonStyles'

/**
 * "Link button" block: a full-width button linking to an external page, solid or outlined.
 * Text and link are read back from the visible <a> (no duplicated JSON), the text goes out
 * as a text node (escaped) and the link/color/variant are validated. Without a valid link
 * no button is emitted; its text is then kept in data-text.
 */
export const LinkButtonNode = Node.create({
  name: 'linkButton',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      text: {
        default: '',
        rendered: false,
        parseHTML: element => (element.querySelector(':scope > a')?.textContent || '').trim()
          || element.getAttribute('data-text') || '',
      },
      url: {
        default: '',
        rendered: false,
        parseHTML: element => safeLinkUrl(element.querySelector(':scope > a')?.getAttribute('href')) || '',
      },
      color: {
        default: DEFAULT_LINK_BUTTON_COLOR,
        rendered: false,
        parseHTML: element => normalizeHexColor(element.getAttribute('data-color')) || DEFAULT_LINK_BUTTON_COLOR,
      },
      variant: {
        default: DEFAULT_LINK_BUTTON_VARIANT,
        rendered: false,
        parseHTML: element => normalizeLinkButtonVariant(element.getAttribute('data-variant')),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="link-button"]' }]
  },

  renderHTML({ node }) {
    const { text } = node.attrs
    const color = normalizeHexColor(node.attrs.color) || DEFAULT_LINK_BUTTON_COLOR
    const variant = normalizeLinkButtonVariant(node.attrs.variant)
    const url = safeLinkUrl(node.attrs.url)

    const attrs = { 'data-type': 'link-button', 'data-color': color, 'data-variant': variant, style: LINK_BUTTON_STYLES.wrapper }
    if (!url) {
      if (text) attrs['data-text'] = text
      return ['div', attrs]
    }

    return ['div', attrs, [
      'a',
      { href: url, target: '_blank', rel: 'noopener noreferrer', style: linkButtonStyle(color, variant) },
      text,
    ]]
  },

  addNodeView() {
    return VueNodeViewRenderer(LinkButtonNodeView)
  },

  addCommands() {
    return {
      insertLinkButton: (attrs = {}) => ({ chain }) => {
        return chain()
          .insertContent({
            type: this.name,
            attrs,
          })
          .run()
      },
    }
  },
})

export default LinkButtonNode
